import { getSessionFromStore, getStore } from 'core_main/store';
import { getSession } from 'core_main/api/sessions';
import { createSharedConversationChannelString } from 'core_main/pubnub/helpers/channelName';
import { handleError } from 'globals/errors';

const getOrFetchSession = (conversationId: string) => {
  const session = getSessionFromStore(conversationId);
  return session
    ? Promise.resolve(session)
    : getSession(conversationId).then(() =>
        getSessionFromStore(conversationId),
      );
};

/**
 * Subscribes to the typing indicator channel for a conversation if typing indicators are enabled.
 * This is used when re-subscribing after customer merges or other events that require channel updates.
 * @param conversationId - The conversation ID to subscribe typing indicators for
 * @param pubnub - The PubNub module instance
 * @returns Promise that resolves when subscription is complete
 */
export const subscribeToTypingIndicatorChannel = (
  conversationId: string,
  pubnub: any,
): Promise<void> => {
  return getOrFetchSession(conversationId)
    .then((conversation) => {
      const pubNubChannels = conversation?.attributes?.pubnub?.data?.channels;
      const store = getStore();
      const { settings } = store;
      const { showTypingIndicatorCustomerWeb, showTypingIndicatorWeb } =
        settings;

      const shouldSubscribeToSharedChannels =
        showTypingIndicatorCustomerWeb || showTypingIndicatorWeb;

      if (!pubNubChannels || !shouldSubscribeToSharedChannels) {
        return;
      }

      const conversationChannel = pubNubChannels.find(
        (channel) => channel.type === 'conversation',
      );

      if (!conversationChannel || !store.isPubNubConnected) {
        return;
      }

      // Build the shared typing channel from the current store identity rather
      // than string-replacing the stored conversation channel name. After a
      // customer merge, `conversationChannel.name` retains the pre-merge
      // customer ID, so the legacy prefix-replace would subscribe to a stale
      // `cust-<oldId>` channel that the Go publisher never targets.
      const { orgId, customerId } = store;
      if (!orgId || !customerId) {
        return;
      }
      const sharedConversationChannelString =
        createSharedConversationChannelString(
          orgId,
          customerId,
          conversationId,
        );

      const metaPubNub = pubnub.getMetaPubNub()?.instance;
      if (metaPubNub) {
        metaPubNub.subscribe({
          channels: [sharedConversationChannelString],
        });
      } else {
        handleError(new Error('MetaPubNub instance not available'), {
          message: `Failed to subscribe to typing indicator channel for conversation ${conversationId}: MetaPubNub instance not available`,
        });
      }
    })
    .catch((error) => {
      // Catch and log errors, but don't block execution as this is a non-critical operation
      // Errors are already logged by getSession if it fails
      handleError(error, {
        message: `Failed to subscribe to typing indicator channel for conversation ${conversationId}`,
      });
    });
};
